import { useContext } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';

import { collectionApiConfig } from '@/config/api/collection';
import getAllFacetsForCollection from '@/entities/collection/api/allGlobalFlatsFacetsV2.graphql';
import getAllSpecsForCollection from '@/entities/collection/api/allGlobalFlatsSpecsV2.graphql';
import allFlatsForCollection from '@/entities/collection/api/allGlobalFlatsV2.graphql';
import { useClientApi } from '@/plugins/api/lk';
import { usePortalApi } from '@/plugins/api/portal';
import {
  fullLogDecorate,
  notifyDecorate,
} from '@/shared/lib';
import { useAgentProfileStore } from '@/store/agentProfile';
import type {
  CollectionFacets,
  CollectionRawLot,
  CollectionRequestParams,
  CollectionSpecs,
  CollectionType,
  CurrentSentCollection,
  SendType,
  SentCollectionResponseData,
} from '@/store/collection/types';
import { COLLECTION_TYPES } from '@/store/collection/types';
import type { PageInfoGraphql } from '@/types/PageInfo';
import type { PaginatedItemsResponse } from '@/types/Response';

export interface CollectionItem {
  Global_id: string;
  comment: string;
}

export const loadCollections = fullLogDecorate(async (params: CollectionRequestParams, type: CollectionType): Promise<SentCollectionResponseData> => {
  const url = collectionApiConfig.collectionListByType(type);
  const { data } = await useClientApi().get<SentCollectionResponseData>(url, {
    params: {
      ...params,
      ordering: params.ordering || '-created_at',
      page_size: 8,
    },
  });

  return data;
}, '🚀 ~ file: Collection/api ~ loadCollections ~ error', true);

export const loadCollectionById = fullLogDecorate(async (id: number, type: CollectionType): Promise<CurrentSentCollection> => {
  const url = collectionApiConfig.detailCollection(id, type);
  const { data } = await useClientApi().get(url);

  return data;
}, '🚀 ~ file: Collection/api ~ loadCollectionById ~ error', true);

export const changeCollectionTitle = notifyDecorate(async (id: number, title: string, type: CollectionType) => {
  const url = collectionApiConfig.detailCollection(id, type);
  await useClientApi().patch(url, {
    name: title,
  });
}, '🚀 ~ file: Collection/api ~ changeCollectionTitle ~ error', true);

export const loadSpecs = fullLogDecorate(async (ids: Array<string>) => {
  return usePortalApi()
    .$post('/graphql/', {
      query: getAllSpecsForCollection.loc?.source.body,
      variables: {
        ids,
      },
    })
    .then((response) => response.data.allGlobalFlatsSpecsV2 as CollectionSpecs);
}, '🚀 ~ file: Collection/api ~ loadSpecs ~ error', true);

export interface AllGlobalFlatsFilters {
  areaMin?: string;
  areaMax?: string;
  fullFinalPriceMin?: string;
  fullFinalPriceMax?: string;
  project?: string[];
  rooms?: string[];
}

export const loadFacets = fullLogDecorate(async (ids: Array<string>) => {
  return usePortalApi()
    .$post('/graphql/', {
      query: getAllFacetsForCollection.loc?.source.body,
      variables: {
        ids,
      },
    })
    .then((response) => response.data.allGlobalFlatsFacetsV2 as CollectionFacets);
}, '🚀 ~ file: Collection/api ~ loadFacets ~ error', true);

export interface FlatsCollectionResponse {
  edges: { node: CollectionRawLot }[];
  pageInfo: PageInfoGraphql;
  totalCount: number;
}

export const loadFlatsForCollection = fullLogDecorate(async (ids: Array<string>, cursor: string = '', filters?: AllGlobalFlatsFilters, pageSize = 6): Promise<FlatsCollectionResponse> => {
  return usePortalApi()
    .$post('/graphql/', {
      query: allFlatsForCollection.loc?.source.body,
      variables: {
        first: pageSize,
        after: cursor,
        ids,
        ...filters,
      },
    })
    .then((response) => response.data.allGlobalFlatsV2);
}, '🚀 ~ file: Collection/api ~ loadFlatsForCollection ~ error', true);

export const addedCollectionComment = notifyDecorate(async (id: number, comment: string) => {
  const url = collectionApiConfig.commentCollection;
  await useClientApi().post(url, {
    profitbase_id: id,
    comment,
  });
}, '🚀 ~ file: Collection/api ~ addedCollectionComment ~ error', true);

export const editedCollectionComment = notifyDecorate(async (id: number, comment: string) => {
  const url = collectionApiConfig.commentCollection;
  await useClientApi().patch(url, {
    profitbase_id: id,
    comment,
  });
}, 'Произошла ошибка при сохранении комментария', true);

interface LotInCollectionItem {
  id: string; is_collections: boolean;
}

export const getAvailableCollectionItems = fullLogDecorate(() => {
  return useClientApi().get<PaginatedItemsResponse<CollectionItem>>(collectionApiConfig.my, {
    params: {
      page_size: 1000,
    },
  });
}, '🚀 ~ file: Collection/api ~ getAvailableCollectionItems ~ error');

export const getUnavailableCollectionItems = fullLogDecorate(() => {
  return useClientApi().get<PaginatedItemsResponse<CollectionItem>>(collectionApiConfig.myUnavailable, {
    params: {
      page_size: 1000,
    },
  });
}, '🚀 ~ file: Collection/api ~ getUnavailableCollectionItems ~ error');

interface IsInCollectionResponse {
  data: Array<LotInCollectionItem>;
}
export const checkIsInCollection = fullLogDecorate((globalId: string | string[]) => {
  return useClientApi().get<IsInCollectionResponse>(collectionApiConfig.contains, {
    params: {
      Global_id: globalId,
    },
  });
}, '🚀 ~ file: Collection/api ~ checkIsInCollection ~ error');

export const addToCollection = notifyDecorate((globalId: string) => {
  return useClientApi().post<LotInCollectionItem>(collectionApiConfig.addOrRemove, { Global_id: globalId });
}, 'Произошла ошибка при добавлении лота в подборку');

export const removeFromCollection = notifyDecorate((globalId: string | string[]) => {
  return useClientApi().delete<LotInCollectionItem>(collectionApiConfig.addOrRemove, { params: { Global_id: globalId } });
}, 'Произошла ошибка при удалении лота из подборки');

export type OffersSelectionResponse = Partial<{
  id: number;
}>;

export const sendOfferSelections = async (propertyGlobalIds: Array<string | number>): Promise<number | undefined> => {
  const profileStore = useAgentProfileStore();
  const { getAccount } = profileStore;
  const { profile } = storeToRefs(profileStore);

  try {
    await getAccount();
    const body: { [key: string]: unknown } = {
      profitbase_ids: propertyGlobalIds,
      contact_first_name: profile.value.name,
      contact_last_name: profile.value.surname,
      contact_phone: profile.value.phone || '',
      contact_email: profile.value.email,
      discounts: {},
    };

    const url = '/api/pdf_selection';
    const response = await useClientApi().$post<OffersSelectionResponse>(url, body);

    if (!response?.id) {
      throw new Error('Offer id was not specified in response');
    }

    return response.id;
  } catch (e) {
    console.error('[OfferClient::sendPdfSelections]', (e as { message?: string })?.message);
  }
};

export function usePrintCollection () {
  const { $config } = useContext();
  return async function printCollection (profitbaseIds: (string | number)[]) {
    const offer_id = await sendOfferSelections(profitbaseIds);
    const {
      MAIN_SITE_HOST,
      PROTOCOL,
    } = $config;
    const brokerUrl = `${ PROTOCOL }broker.${ MAIN_SITE_HOST }`;
    const pdfUrl = `${ PROTOCOL }${ MAIN_SITE_HOST }/pdf/?url=${ brokerUrl }/print/commercial-offer/${ offer_id }`;
    return pdfUrl;
  };
}

interface SendCollectionResponse {
  id: number;
  title: string;
  client_id: number;
  agent_id: number;
}

export interface SendCollectionPayload {
  clientId: number;
  objects: string[];
  managerFio?: string;
  source: string;
  sendType: SendType;

}
export const sendCollection = notifyDecorate((payload: SendCollectionPayload) => {
  const url = collectionApiConfig.collectionListByType(COLLECTION_TYPES.sends);
  const body = {
    client_id: payload.clientId,
    objects: payload.objects,
    send_type: payload.sendType,
    source: payload.source,
    managerFio: payload.managerFio,
  };

  return useClientApi().post<SendCollectionResponse>(url, Object.fromEntries(Object.entries(body).filter(([_, value]) => value)));
}, 'Произошла ошибка при отправке подборки');

interface ViewedStatus {
  hasUnviewed: boolean;
}

export const loadStatusViewedCollection = fullLogDecorate(async (bookingId?: number): Promise<boolean> => {
  const url = collectionApiConfig.unViewedCollectionStatus;
  const { data } = await useClientApi().get<ViewedStatus>(url, {
    params: {
      bookingId,
    },
  });

  return data.hasUnviewed;
}, '🚀 ~ file: Collection/api ~ loadStatusViewedCollection ~ error', true);

export const setViewedCollection = fullLogDecorate(async (bookingId?: number): Promise<void> => {
  const url = collectionApiConfig.viewedCollection;
  const body = bookingId ? { bookingId } : undefined;
  await useClientApi().post(url, body);
}, '🚀 ~ file: Collection/api ~ setViewedCollection ~ error', true);
